import type {
  ListResponse,
  PackAssignPayload,
  PackingSlipDocument,
  PickClaimPayload,
  PickConfirmPayload,
  PickListDocument,
  PickRound,
  PickRoundBuild,
  PickRoundCandidate,
  PickRoundConfirm,
  PickShortPayload,
  PickStagePayload,
  PickTask,
  PickingFlow,
  PickingFlowCreate,
  PickingFlowTarget,
  PickingFlowUpdate,
  ReceptionClaimPayload,
  ReceptionClaimResult,
  ReceptionConfirmPayload,
  ReceptionView,
  Shipment,
  ShipmentBookPayload,
  ShipmentCreate,
  ShipmentUpdate,
  StagingPlan,
} from '@warehouse/shared';

import { api } from '../../lib/api-client';
import { listQueryKey, listQueryParams, type ListState } from '../../lib/list-query';
import { commandId } from '../../lib/command-id';

/**
 * The module's data layer.
 *
 * Every mutation sends a client-generated `commandId`. That is not ceremony:
 * the server dedupes on it (`command_dedupe`), so a double-tapped "Bekräfta" on
 * a warehouse tablet with a flaky AP cannot pick the same goods twice — the
 * exact guarantee the Android outbox relies on (ARCHITECTURE §4/§6).
 */

export const pickingKeys = {
  flows: ['picking', 'flows'] as const,
  flow: (id: string) => ['picking', 'flows', id] as const,
  flowTargets: ['picking', 'flows', 'targets'] as const,
  rounds: (state: ListState) => ['picking', 'rounds', listQueryKey(state)] as const,
  round: (id: string) => ['picking', 'rounds', id] as const,
  candidates: (deliveryDate: string) => ['picking', 'rounds', 'candidates', deliveryDate] as const,
  staging: (roundId: string) => ['picking', 'rounds', roundId, 'staging'] as const,
  pickList: (roundId: string) => ['picking', 'rounds', roundId, 'pick-list'] as const,
  tasks: (state: ListState) => ['picking', 'tasks', listQueryKey(state)] as const,
  shipments: (state: ListState) => ['shipments', listQueryKey(state)] as const,
  shipment: (id: string) => ['shipments', id] as const,
  packingSlip: (id: string) => ['shipments', id, 'packing-slip'] as const,
  reception: ['reception'] as const,
  receptionView: (token: string) => ['reception', token] as const,
} as const;

// ---------------------------------------------------------------------------
// picking flows
// ---------------------------------------------------------------------------

export const flowsApi = {
  list: () => api.get<PickingFlow[]>('/picking/flows'),
  get: (id: string) => api.get<PickingFlow>(`/picking/flows/${id}`),
  targets: () => api.get<PickingFlowTarget[]>('/picking/flows/targets'),
  create: (input: PickingFlowCreate) =>
    api.post<PickingFlow>('/picking/flows', { ...input, commandId: commandId() }),
  update: (id: string, input: PickingFlowUpdate) =>
    api.put<PickingFlow>(`/picking/flows/${id}`, { ...input, commandId: commandId() }),
  setDefault: (id: string) =>
    api.post<PickingFlow>(`/picking/flows/${id}/default`, { commandId: commandId() }),
  remove: (id: string) => api.delete<void>(`/picking/flows/${id}`),
};

// ---------------------------------------------------------------------------
// rounds + tasks
// ---------------------------------------------------------------------------

export const roundsApi = {
  list: (state: ListState) =>
    api.get<ListResponse<PickRound>>('/picking/rounds', { query: listQueryParams(state) }),
  get: (id: string) => api.get<PickRound>(`/picking/rounds/${id}`),
  candidates: (deliveryDate: string) =>
    api.get<PickRoundCandidate[]>('/picking/rounds/candidates', { query: { deliveryDate } }),
  build: (input: PickRoundBuild) =>
    api.post<PickRound[]>('/picking/rounds', { ...input, commandId: commandId() }),
  start: (id: string) => api.post<PickRound>(`/picking/rounds/${id}/start`, { commandId: commandId() }),
  close: (id: string) => api.post<PickRound>(`/picking/rounds/${id}/close`, { commandId: commandId() }),
  remove: (id: string) => api.delete<void>(`/picking/rounds/${id}`),

  staging: (id: string) => api.get<StagingPlan>(`/picking/rounds/${id}/staging`),
  stage: (id: string, input: Omit<PickStagePayload, 'roundId'>) =>
    api.post<{ movements: number; toBinId: string }>(`/picking/rounds/${id}/stage`, {
      ...input,
      commandId: commandId(),
    }),

  pickList: (id: string) => api.get<PickListDocument>(`/picking/rounds/${id}/pick-list`),
  print: (id: string) =>
    api.post<{ jobId: string | null; routed: boolean; reason?: string }>(`/picking/rounds/${id}/print`),
  confirmRound: (id: string, input: PickRoundConfirm) =>
    api.post<{ confirmed: number; short: number }>(`/picking/rounds/${id}/confirm`, {
      ...input,
      commandId: commandId(),
    }),
};

export const tasksApi = {
  list: (state: ListState) =>
    api.get<ListResponse<PickTask>>('/picking/tasks', { query: listQueryParams(state) }),
  claim: (input: PickClaimPayload) =>
    api.post<{ claimed: string[] }>('/picking/tasks/claim', { ...input, commandId: commandId() }),
  release: (input: PickClaimPayload) =>
    api.post<{ released: string[] }>('/picking/tasks/release', { ...input, commandId: commandId() }),
  confirm: (taskId: string, input: Omit<PickConfirmPayload, 'taskId'>) =>
    api.post<{ status: string; pickedQtyBase: string }>(`/picking/tasks/${taskId}/confirm`, {
      ...input,
      commandId: commandId(),
    }),
  short: (taskId: string, input: Omit<PickShortPayload, 'taskId'>) =>
    api.post<{ status: string; pickedQtyBase: string }>(`/picking/tasks/${taskId}/short`, {
      ...input,
      commandId: commandId(),
    }),
};

// ---------------------------------------------------------------------------
// shipments
// ---------------------------------------------------------------------------

export const shipmentsApi = {
  list: (state: ListState) =>
    api.get<ListResponse<Shipment>>('/shipments', { query: listQueryParams(state) }),
  get: (id: string) => api.get<Shipment>(`/shipments/${id}`),
  create: (input: ShipmentCreate) =>
    api.post<Shipment>('/shipments', { ...input, commandId: commandId() }),
  update: (id: string, input: ShipmentUpdate) =>
    api.put<Shipment>(`/shipments/${id}`, { ...input, commandId: commandId() }),

  assignPallet: (id: string, input: Omit<PackAssignPayload, 'shipmentId'>) =>
    api.post<Shipment>(`/shipments/${id}/pallets`, { ...input, commandId: commandId() }),
  removePallet: (id: string, palletId: string) =>
    api.delete<Shipment>(`/shipments/${id}/pallets/${palletId}`),
  pack: (id: string) => api.post<Shipment>(`/shipments/${id}/pack`, { commandId: commandId() }),

  book: (id: string, input: Omit<ShipmentBookPayload, 'shipmentId'>) =>
    api.post<Shipment>(`/shipments/${id}/book`, { ...input, commandId: commandId() }),
  cancel: (id: string, reason?: string) =>
    api.post<Shipment>(`/shipments/${id}/cancel`, { reason, commandId: commandId() }),
  ship: (id: string) => api.post<Shipment>(`/shipments/${id}/ship`, { commandId: commandId() }),

  packingSlip: (id: string) => api.get<PackingSlipDocument>(`/shipments/${id}/packing-slip`),
  documentUrl: (id: string, documentId: string) => `/api/shipments/${id}/documents/${documentId}`,
  printLabels: (id: string) =>
    api.post<{ routed: boolean; reason?: string }>(`/shipments/${id}/print/labels`),
  printPackingSlip: (id: string) =>
    api.post<{ routed: boolean; reason?: string }>(`/shipments/${id}/print/packing-slip`),
};

// ---------------------------------------------------------------------------
// reception (portal)
// ---------------------------------------------------------------------------

export const receptionApi = {
  inbox: () => api.get<ReceptionView[]>('/reception'),
  view: (qrToken: string) => api.get<ReceptionView>(`/reception/${qrToken}`),
  confirm: (qrToken: string, input: Omit<ReceptionConfirmPayload, 'qrToken'>) =>
    api.post<ReceptionView>(`/reception/${qrToken}/confirm`, { ...input, commandId: commandId() }),
  claim: (qrToken: string, input: Omit<ReceptionClaimPayload, 'qrToken'>) =>
    api.post<ReceptionClaimResult>(`/reception/${qrToken}/claim`, { ...input, commandId: commandId() }),
};
